import {
	BodyMSemiBold,
	cn,
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
	Icon,
} from '@lqa-frontend/ui-system';
import React from 'react';
import {
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from './SidebarMenu';
import type { SidebarMenuSubButtonSize } from './types';

interface ISidebarCollapsibleMenuProps {
	defaultOpen?: boolean;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	className?: string;
	children: React.ReactNode;
}

interface ISidebarCollapsibleMenuTriggerProps {
	label: string;
	icon?: React.ReactNode;
	tooltip?: string;
	className?: string;
}

interface ISidebarCollapsibleMenuContentProps {
	className?: string;
	children: React.ReactNode;
}

interface ISidebarCollapsibleMenuItemProps {
	asChild?: boolean;
	isActive?: boolean;
	size?: SidebarMenuSubButtonSize;
	className?: string;
	children: React.ReactNode;
}

function SidebarCollapsibleMenuRoot({
	defaultOpen = false,
	isOpen,
	onOpenChange,
	className,
	children,
}: Readonly<ISidebarCollapsibleMenuProps>) {
	return (
		<Collapsible
			defaultOpen={defaultOpen}
			open={isOpen}
			onOpenChange={onOpenChange}
			className={cn(
				'group/collapsible rounded-s data-[state=open]:bg-sidebar-primary',
				className
			)}
		>
			<SidebarMenuItem>{children}</SidebarMenuItem>
		</Collapsible>
	);
}

function SidebarCollapsibleMenuTrigger({
	label,
	icon,
	tooltip,
	className,
}: Readonly<ISidebarCollapsibleMenuTriggerProps>) {
	return (
		<CollapsibleTrigger asChild>
			<SidebarMenuButton
				tooltip={tooltip ?? label}
				className={cn(
					'flex flex-row items-center gap-16',
					'bg-sidebar text-sidebar-accent-foreground/75',
					'hover:bg-sidebar-primary hover:text-sidebar-accent-foreground/75',
					'data-[state=open]:bg-sidebar-primary data-[state=open]:text-sidebar-accent-foreground data-[state=open]:[&>svg]:text-sidebar-accent-foreground',
					'data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground',
					className
				)}
			>
				{icon}
				<BodyMSemiBold hasEllipsis className="text-inherit">
					{label}
				</BodyMSemiBold>
				<Icon
					name="chevronRight"
					className="ml-auto size-12! transition-transform group-data-[state=open]/collapsible:-rotate-90"
				/>
			</SidebarMenuButton>
		</CollapsibleTrigger>
	);
}

function SidebarCollapsibleMenuContent({
	className,
	children,
}: Readonly<ISidebarCollapsibleMenuContentProps>) {
	return (
		<CollapsibleContent className={className}>
			<SidebarMenuSub>{children}</SidebarMenuSub>
		</CollapsibleContent>
	);
}

function SidebarCollapsibleMenuItem({
	asChild = false,
	isActive = false,
	size,
	className,
	children,
}: Readonly<ISidebarCollapsibleMenuItemProps>) {
	return (
		<SidebarMenuSubItem>
			<SidebarMenuSubButton
				asChild={asChild}
				isActive={isActive}
				size={size}
				className={cn('pl-50', className)}
			>
				{children}
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	);
}

const SidebarCollapsibleMenu = Object.assign(SidebarCollapsibleMenuRoot, {
	Trigger: SidebarCollapsibleMenuTrigger,
	Content: SidebarCollapsibleMenuContent,
	Item: SidebarCollapsibleMenuItem,
});

export { SidebarCollapsibleMenu };
export type {
	ISidebarCollapsibleMenuProps,
	ISidebarCollapsibleMenuTriggerProps,
	ISidebarCollapsibleMenuContentProps,
	ISidebarCollapsibleMenuItemProps,
};
